import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OpeningHoursEntry, OpeningTimeRecurrence, RecurrenceFrequency } from '../../types/Location';
import { emptyLocalizedText } from '../../types/LocalizedText';
import { isDefaultRecurrence } from '../../utils/openingTimeUtils';
import { DayValue } from '../../utils/dayUtils';

interface OpeningHoursRecurrenceDialogProps {
  open: boolean;
  entry: OpeningHoursEntry | null;
  day: DayValue;
  languageCodes: string[];
  onClose: () => void;
  onSave: (updates: Pick<OpeningHoursEntry, 'recurrence' | 'note'>) => void;
}

const FREQUENCIES: RecurrenceFrequency[] = ['weekly', 'monthly', 'once'];
const MONTHLY_POSITIONS = ['1', '2', '3', '4', '-1'] as const;

function getTodayDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function withDefaultStartDate(recurrence: OpeningTimeRecurrence): OpeningTimeRecurrence {
  return recurrence.startDate ? recurrence : { ...recurrence, startDate: getTodayDateString() };
}

const OpeningHoursRecurrenceDialog: React.FC<OpeningHoursRecurrenceDialogProps> = ({
  open,
  entry,
  day,
  languageCodes,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [useCustomRecurrence, setUseCustomRecurrence] = useState(false);
  const [recurrence, setRecurrence] = useState<OpeningTimeRecurrence>({});
  const [note, setNote] = useState(emptyLocalizedText(languageCodes));
  const [startDateError, setStartDateError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !entry) {
      return;
    }

    const hasCustomRecurrence = !isDefaultRecurrence(entry.recurrence);
    setUseCustomRecurrence(hasCustomRecurrence);

    let initialRecurrence = entry.recurrence ?? { frequency: 'weekly', interval: 1 };
    if (initialRecurrence.frequency === 'monthly' && initialRecurrence.monthlyPosition === undefined) {
      initialRecurrence = { ...initialRecurrence, monthlyPosition: 1 };
    }
    if (hasCustomRecurrence) {
      initialRecurrence = withDefaultStartDate(initialRecurrence);
    }

    setRecurrence(initialRecurrence);
    setNote({ ...emptyLocalizedText(languageCodes), ...entry.note });
    setStartDateError(null);
  }, [open, entry, languageCodes]);

  const frequency = recurrence.frequency ?? 'weekly';

  const updateRecurrence = (updates: Partial<OpeningTimeRecurrence>) => {
    setRecurrence((current) => {
      const next = { ...current, ...updates };

      if (updates.frequency === 'monthly' && next.monthlyPosition === undefined) {
        next.monthlyPosition = 1;
      }

      return next;
    });

    if (updates.startDate) {
      setStartDateError(null);
    }
  };

  const handleSave = () => {
    if (useCustomRecurrence && !recurrence.startDate) {
      setStartDateError(t('openingHoursTable.recurrence.startDateRequired'));
      return;
    }

    const savedRecurrence = useCustomRecurrence
      ? {
          ...recurrence,
          ...(frequency === 'monthly'
            ? { monthlyPosition: recurrence.monthlyPosition ?? 1 }
            : {}),
        }
      : undefined;

    onSave({
      recurrence: savedRecurrence,
      note: Object.values(note).some((text) => text.trim()) ? note : undefined,
    });
    onClose();
  };

  const dayLabel = t(`openingHours.${day}`);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('openingHoursTable.recurrence.title')}</DialogTitle>
      <DialogContent>
        <FormControlLabel
          sx={{ mt: 1, mb: 2, display: 'flex' }}
          control={
            <Switch
              checked={useCustomRecurrence}
              onChange={(event) => {
                const enabled = event.target.checked;
                setUseCustomRecurrence(enabled);
                if (enabled) {
                  setRecurrence((current) => withDefaultStartDate(current));
                  setStartDateError(null);
                }
              }}
            />
          }
          label={t('openingHoursTable.recurrence.useCustom')}
        />

        {useCustomRecurrence && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('openingHoursTable.recurrence.frequency')}</InputLabel>
              <Select
                value={frequency}
                label={t('openingHoursTable.recurrence.frequency')}
                onChange={(event) =>
                  updateRecurrence({
                    frequency: event.target.value as RecurrenceFrequency,
                  })
                }
              >
                {FREQUENCIES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`openingHoursTable.recurrence.frequencies.${value}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(frequency === 'weekly' || frequency === 'monthly') && (
              <TextField
                type="number"
                size="small"
                label={t('openingHoursTable.recurrence.interval')}
                value={recurrence.interval ?? 1}
                onChange={(event) =>
                  updateRecurrence({
                    interval: Math.max(1, Number(event.target.value) || 1),
                  })
                }
                inputProps={{ min: 1 }}
                helperText={
                  frequency === 'weekly'
                    ? t('openingHoursTable.recurrence.intervalWeeklyHelp')
                    : t('openingHoursTable.recurrence.intervalMonthlyHelp')
                }
              />
            )}

            {frequency === 'monthly' && (
              <FormControl fullWidth size="small">
                <InputLabel>{t('openingHoursTable.recurrence.monthlyPosition')}</InputLabel>
                <Select
                  value={String(recurrence.monthlyPosition ?? 1)}
                  label={t('openingHoursTable.recurrence.monthlyPosition')}
                  onChange={(event) =>
                    updateRecurrence({
                      monthlyPosition: Number(event.target.value),
                    })
                  }
                >
                  {MONTHLY_POSITIONS.map((position) => (
                    <MenuItem key={position} value={position}>
                      {t(`openingHoursTable.monthlyPositions.${position}`, { day: dayLabel })}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('openingHoursTable.recurrence.monthlyPositionHelp')}</FormHelperText>
              </FormControl>
            )}

            <TextField
              type="date"
              size="small"
              label={t('openingHoursTable.recurrence.startDate')}
              value={recurrence.startDate ?? ''}
              onChange={(event) => updateRecurrence({ startDate: event.target.value || undefined })}
              InputLabelProps={{ shrink: true }}
              required={useCustomRecurrence}
              error={Boolean(startDateError)}
              helperText={
                startDateError || t('openingHoursTable.recurrence.startDateRequiredHelp')
              }
            />

            {frequency !== 'once' && (
              <TextField
                type="date"
                size="small"
                label={t('openingHoursTable.recurrence.endDate')}
                value={recurrence.endDate ?? ''}
                onChange={(event) => updateRecurrence({ endDate: event.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
                helperText={t('openingHoursTable.recurrence.endDateHelp')}
              />
            )}
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('openingHoursTable.recurrence.note')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {languageCodes.map((code) => (
            <TextField
              key={code}
              size="small"
              label={code.toUpperCase()}
              value={note[code] ?? ''}
              onChange={(event) =>
                setNote((current) => ({ ...current, [code]: event.target.value }))
              }
              multiline
              minRows={2}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={handleSave} variant="contained">
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OpeningHoursRecurrenceDialog;
